import { Box } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

import type { ScoutSubmitHandler } from "./ScoutInput";

import { useScoutAllowMutations } from "../hooks/useScoutAllowMutations";
import { useScoutConversation } from "../hooks/useScoutConversation";
import { mapScoutAttachmentToPreview } from "../scoutSubmitAttachments";
import { ChatChrome } from "./ChatChrome";
import { NewChat } from "./NewChat";
import { ScoutTenantDocumentProposalProvider } from "./ScoutTenantDocumentProposalContext";

export type ScoutConversationSettingsState = {
  allowMutations: boolean;
  onAllowMutationsChange: (next: boolean) => void;
  settingsDisabled: boolean;
};

export const ScoutChat = ({
  onHasMessagesChange,
  onConversationTitleChange,
  onConversationSettingsChange,
}: {
  onHasMessagesChange?: (hasMessages: boolean) => void;
  onConversationTitleChange?: (input: {
    conversationId: string | undefined;
    title: string | undefined;
  }) => void;
  onConversationSettingsChange?: (settings: ScoutConversationSettingsState) => void;
}) => {
  const navigate = useNavigate();
  const { tenantId, conversationId } = useParams();
  const handleConversationIdChange = useCallback(
    (nextConversationId: string) => {
      if (!tenantId) {
        return;
      }

      navigate(`/o/${tenantId}/scout/${nextConversationId}`, { replace: true });
    },
    [navigate, tenantId]
  );
  const {
    conversation,
    messages,
    activeTurn,
    streamStatus,
    loading,
    isSending,
    isAborting,
    isLoadingOlderMessages,
    hasOlderMessages,
    sendMessage,
    abortTurn,
    loadOlderMessages,
    privateDocuments,
    largeDocumentOptIns,
    dismissLargeDocumentOptIn,
    confirmLargeDocumentAnalysis,
    isConfirmingLargeDocumentAnalysis,
    isDecliningLargeDocumentAnalysis,
    registerComposerAttachments,
    unregisterComposerAttachment,
    respondedTenantDocumentProposals,
    isProcessingTenantDocumentProposal,
    confirmTenantDocumentProposal,
    declineTenantDocumentProposal,
  } = useScoutConversation({
    tenantId,
    conversationId,
    onConversationIdChange: handleConversationIdChange,
  });
  const tenantDocumentProposalContextValue = useMemo(
    () => ({
      respondedProposals: respondedTenantDocumentProposals,
      isProcessing: isProcessingTenantDocumentProposal,
      onConfirm: confirmTenantDocumentProposal,
      onDecline: declineTenantDocumentProposal,
    }),
    [
      confirmTenantDocumentProposal,
      declineTenantDocumentProposal,
      isProcessingTenantDocumentProposal,
      respondedTenantDocumentProposals,
    ]
  );
  const hasMessages = messages.length > 0;
  const isConversationRoute = Boolean(conversationId);
  const isLoadingConversation = isConversationRoute && loading && !hasMessages;
  useEffect(() => {
    onHasMessagesChange?.(hasMessages);
  }, [hasMessages, onHasMessagesChange]);
  useEffect(() => {
    onConversationTitleChange?.({
      conversationId: conversation?.id,
      title: conversation?.title,
    });
  }, [conversation?.id, conversation?.title, onConversationTitleChange]);
  const isTurnRunning =
    isSending ||
    isAborting ||
    activeTurn?.status === "starting" ||
    activeTurn?.status === "streaming";

  const { allowMutations, onAllowMutationsChange } = useScoutAllowMutations({
    conversationId,
    conversationAllowMutations: conversation?.allowMutations,
  });

  useEffect(() => {
    onConversationSettingsChange?.({
      allowMutations,
      onAllowMutationsChange,
      settingsDisabled: isTurnRunning,
    });
  }, [allowMutations, isTurnRunning, onAllowMutationsChange, onConversationSettingsChange]);

  const handleSubmit = useCallback<ScoutSubmitHandler>(
    async (message, options) => {
      const attachments = options?.attachments;
      await sendMessage(message, {
        allowMutations,
        attachmentIds: attachments?.map((attachment) => attachment.privateDocumentId),
        attachmentPreviews:
          options?.attachmentPreviews ?? attachments?.map(mapScoutAttachmentToPreview),
        extractionWatchPrivateDocumentIds: options?.extractionWatchPrivateDocumentIds,
      });
    },
    [allowMutations, sendMessage]
  );

  return (
    <ScoutTenantDocumentProposalProvider value={tenantDocumentProposalContextValue}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          width: "100%",
          alignItems: "center",
        }}
      >
        {!hasMessages && !isConversationRoute ? (
          <NewChat
            tenantId={tenantId}
            onSubmit={handleSubmit}
            onAbort={abortTurn}
            isSending={isTurnRunning}
            isAborting={isAborting}
            onAttachmentsUploaded={registerComposerAttachments}
            onAttachmentRemoved={unregisterComposerAttachment}
            allowMutations={allowMutations}
            onAllowMutationsChange={onAllowMutationsChange}
          />
        ) : (
          <ChatChrome
            tenantId={tenantId}
            conversationId={conversationId}
            messages={messages}
            activeTurn={activeTurn}
            streamStatus={streamStatus}
            loading={isLoadingConversation}
            initialPinned={isConversationRoute}
            onSubmit={handleSubmit}
            onAbort={abortTurn}
            isSending={isTurnRunning}
            isAborting={isAborting}
            onLoadOlderMessages={loadOlderMessages}
            isLoadingOlderMessages={isLoadingOlderMessages}
            hasOlderMessages={hasOlderMessages}
            privateDocuments={privateDocuments}
            largeDocumentOptIns={largeDocumentOptIns}
            onDismissLargeDocumentOptIn={dismissLargeDocumentOptIn}
            onConfirmLargeDocumentAnalysis={confirmLargeDocumentAnalysis}
            isConfirmingLargeDocumentAnalysis={isConfirmingLargeDocumentAnalysis}
            isDecliningLargeDocumentAnalysis={isDecliningLargeDocumentAnalysis}
            onAttachmentsUploaded={registerComposerAttachments}
            onAttachmentRemoved={unregisterComposerAttachment}
            allowMutations={allowMutations}
            onAllowMutationsChange={onAllowMutationsChange}
          />
        )}
      </Box>
    </ScoutTenantDocumentProposalProvider>
  );
};
